import React, { useState, useEffect, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { decodeToken } from 'react-jwt';
import axios from 'axios';
import config from '../config/config';
import Header from '../others/Header';
import { useToast } from '../components/ToastContext';
import { FaPrint, FaPlus, FaMinus, FaCog } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import ConfigServicosModal from './ConfigServicosModal';

// ─── CSS global: esconde TUDO que não é a folha na impressão ────────────────
const GlobalPrintStyle = createGlobalStyle`
  @media print {
    input::placeholder,
    textarea::placeholder {
      color: transparent !important;
      opacity: 0 !important;
    }
    input, textarea {
      box-shadow: none !important;
      outline: none !important;
    }
    input[type="checkbox"] {
      display: none !important;
    }
  }
`;

// ─── Mock da empresa ──────────────────────────────────────────────────────────
const EMPRESA_MOCK = {
  codigo:       'CRVA0135',
  nomeFantasia: 'CANGUÇU - RS',
  endereco:     'RUA GENERAL CÂMARA',
  numero:       '970',
  ddd:          '53',
  telefone:     '32527818',
};

// ─── Componente ───────────────────────────────────────────────────────────────
const ProtocoloAtendimento = () => {
  const { addToast } = useToast();
  const { API_BASE_URL } = config;
  const [searchParams] = useSearchParams();

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

  const [userData, setUserData]               = useState(null);
  const [empresa]                             = useState(EMPRESA_MOCK);
  const [numeroProtocolo, setNumeroProtocolo] = useState(null);
  const [dataAtual]                           = useState(formatarData(new Date()));
  const [horaAtual]                           = useState(formatarHora(new Date()));
  const [encaminhante, setEncaminhante]       = useState('');
  // ── NOVO: nome do cliente, campo próprio abaixo do encaminhante. Só vem
  // preenchido quando a navegação chega pelo botão "Gerar Novo Protocolo de
  // Atendimento" do modal de sucesso da Ordem de Serviço, que abre esta página
  // em nova aba com ?cliente=NOME na URL (state do react-router não sobrevive
  // entre abas). Se a página for aberta pelo menu/home, o parâmetro não existe
  // e o campo nasce vazio normalmente. ──
  const [nomeCliente, setNomeCliente]         = useState(searchParams.get('cliente') || '');
  const [tipoIdentificador, setTipoIdentificador] = useState('placa'); // 'placa' | 'chassi'
  const [identificador, setIdentificador]     = useState('');
  const [temPdf, setTemPdf]                   = useState(false);
  const [qtdPdf, setQtdPdf]                   = useState(1);

  // Lista de serviços vinda do backend (configurável via engrenagem)
  const [servicosConfig, setServicosConfig]   = useState([]);
  // Seleção/texto de cada serviço, indexado por id: { [id]: { selecionado, texto } }
  const [selecoesServicos, setSelecoesServicos] = useState({});
  const [modalConfigAberto, setModalConfigAberto] = useState(false);

  const [linhasObs, setLinhasObs]             = useState(['', '', '', '', '']);
  const [gerando, setGerando]                 = useState(false);

  // Ref para addToast: evita que buscarServicos seja recriada a cada render
  // (o que causaria um loop infinito de requisições caso addToast não seja
  // uma referência estável vinda do ToastContext).
  const addToastRef = React.useRef(addToast);
  useEffect(() => { addToastRef.current = addToast; }, [addToast]);

  // ─── Busca a lista de serviços configuráveis ───────────────────────────────
  const buscarServicos = useCallback(function() {
    return axios.get(`${API_BASE_URL}/servicoProtocolo`, getAuthHeader())
      .then(function(res) {
        setServicosConfig(res.data);
      })
      .catch(function() {
        if (addToastRef.current) addToastRef.current('Erro ao carregar lista de serviços', 'error');
      });
  }, [API_BASE_URL]);

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      const decoded = decodeToken(token);
      setUserData(decoded);
    }
    buscarServicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Registra listener afterprint uma única vez para disparar o toast
  // APÓS o diálogo de impressão fechar — nunca durante a impressão.
  const afterPrintRef = React.useRef(null);
  useEffect(() => {
    return () => {
      if (afterPrintRef.current) {
        window.removeEventListener('afterprint', afterPrintRef.current);
      }
    };
  }, []);

  function toggleServico(id) {
    setSelecoesServicos(function(prev) {
      const atual = prev[id] || { selecionado: false, texto: '' };
      return Object.assign({}, prev, {
        [id]: Object.assign({}, atual, { selecionado: !atual.selecionado })
      });
    });
  }

  function alterarTextoServico(id, valor) {
    setSelecoesServicos(function(prev) {
      const atual = prev[id] || { selecionado: false, texto: '' };
      return Object.assign({}, prev, {
        [id]: Object.assign({}, atual, { texto: valor })
      });
    });
  }

  const handleObsChange = (i, v) =>
    setLinhasObs(function(prev) { var n = prev.slice(); n[i] = v; return n; });

  // ── ATENÇÃO: o backend hoje (GET /protocolo) só cria o número sequencial e
  // devolve o registro — não recebe corpo nenhum. Encaminhante, cliente,
  // placa/chassi, serviços marcados e observações continuam existindo só na
  // tela/impressão, sem serem enviados/salvos no servidor por enquanto. Quando
  // o endpoint for expandido para aceitar esses dados, é só incluir o payload
  // na chamada abaixo. ──
  const handleGerarProtocolo = useCallback(function() {
    if (gerando) return;
    setGerando(true);

    axios.get(`${API_BASE_URL}/protocolo`, getAuthHeader())
      .then(function(res) {
        // TODO: confirmar o nome exato do campo do número no retorno do backend.
        // Assumindo "numero" por enquanto — ajustar aqui se for outro nome.
        const numeroGerado = res.data.numero;
        setNumeroProtocolo(numeroGerado);

        // Remove listener anterior se existir
        if (afterPrintRef.current) {
          window.removeEventListener('afterprint', afterPrintRef.current);
        }
        // afterprint dispara DEPOIS que o diálogo de impressão fecha
        afterPrintRef.current = function() {
          setGerando(false);
          window.removeEventListener('afterprint', afterPrintRef.current);
        };
        window.addEventListener('afterprint', afterPrintRef.current);

        setTimeout(function() { window.print(); }, 80);
      })
      .catch(function(err) {
        console.error('Erro ao gerar protocolo:', err);
        if (addToastRef.current) addToastRef.current('Erro ao gerar protocolo', 'error');
        setGerando(false);
      });
  }, [gerando, API_BASE_URL]);

  return (
    <PageWrapper>
      <GlobalPrintStyle />
      <PrintHide>
        <Header />
      </PrintHide>

      <PrintHide>
        <AcoesBar>
          <AcoesTitle>Protocolo de Atendimento</AcoesTitle>
          <BotaoImprimir onClick={handleGerarProtocolo} disabled={gerando}>
            <FaPrint style={{ marginRight: 8 }} />
            {gerando ? 'AGUARDE...' : 'GERAR PROTOCOLO'}
          </BotaoImprimir>
        </AcoesBar>
      </PrintHide>

      <Folha>

        <EngrenagemBtn
          onClick={function() { setModalConfigAberto(true); }}
          title="Configurar serviços"
        >
          <FaCog size={13} />
        </EngrenagemBtn>

        <TopoGrid>
          <EsquerdaBloco>
            <EmpresaNome>{empresa.codigo} {empresa.nomeFantasia}</EmpresaNome>
            <EmpresaInfo>{empresa.endereco}, {empresa.numero}</EmpresaInfo>
            <EmpresaInfo>({empresa.ddd}) {empresa.telefone}</EmpresaInfo>
            <UsuarioNome>{userData ? userData.nomeUsuario : 'USUÁRIO LOGADO'}</UsuarioNome>
          </EsquerdaBloco>

          <DireitaBloco>
            <NumeroQuadro>{numeroProtocolo !== null ? numeroProtocolo : '...'}</NumeroQuadro>
            <DataTexto>DATA {dataAtual} às {horaAtual}h</DataTexto>
            <EncaminhanteWrapper>
              <EncaminhanteInput
                type="text"
                value={encaminhante}
                onChange={function(e) { setEncaminhante(e.target.value); }}
                maxLength={40}
              />
              <EncaminhanteLabel>NOME DO ENCAMINHANTE</EncaminhanteLabel>
            </EncaminhanteWrapper>
            {/* ── NOVO: campo separado do encaminhante — o cliente pode ser um
                despachante enquanto quem encaminha é outro funcionário. ── */}
            <ClienteWrapper>
              <ClienteInput
                type="text"
                value={nomeCliente}
                onChange={function(e) { setNomeCliente(e.target.value); }}
                maxLength={40}
              />
              <ClienteLabel>NOME DO CLIENTE</ClienteLabel>
            </ClienteWrapper>
          </DireitaBloco>
        </TopoGrid>

        <Divisor />

        <PdfRow>
          <PdfLabel>
            <PdfCheckbox
              type="checkbox"
              checked={temPdf}
              onChange={function(e) { setTemPdf(e.target.checked); }}
            />
            PDF
          </PdfLabel>
          {temPdf && (
            <>
              <QtdBox>{qtdPdf}</QtdBox>
              <RoundBtn onClick={function() { setQtdPdf(function(q) { return Math.max(1, q - 1); }); }} title="Diminuir">
                <FaMinus size={9} />
              </RoundBtn>
              <RoundBtn onClick={function() { setQtdPdf(function(q) { return q + 1; }); }} title="Aumentar">
                <FaPlus size={9} />
              </RoundBtn>
            </>
          )}
        </PdfRow>

        {/* Versão impressão da linha PDF: só aparece no @media print */}
        <PdfRowPrint>
          {temPdf
            ? <PdfPrintTexto>PDF: {qtdPdf} via{qtdPdf > 1 ? 's' : ''}</PdfPrintTexto>
            : null
          }
        </PdfRowPrint>

        {/* ── Campo PLACA / CHASSI ── */}
        <PlacaRow>
          <TipoToggle>
            <TipoBtn
              ativo={tipoIdentificador === 'placa'}
              onClick={function() { setTipoIdentificador('placa'); setIdentificador(function(v) { return v.slice(0, 8); }); }}
            >
              PLACA
            </TipoBtn>
            <TipoBtn
              ativo={tipoIdentificador === 'chassi'}
              onClick={function() { setTipoIdentificador('chassi'); }}
            >
              CHASSI
            </TipoBtn>
          </TipoToggle>
          <PlacaLabel>{tipoIdentificador === 'placa' ? 'PLACA:' : 'CHASSI:'}</PlacaLabel>
          <PlacaInput
            type="text"
            value={identificador}
            onChange={function(e) {
              const bruto = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
              const limite = tipoIdentificador === 'placa' ? 7 : 17;
              setIdentificador(bruto.slice(0, limite));
            }}
            maxLength={tipoIdentificador === 'placa' ? 7 : 17}
            placeholder={tipoIdentificador === 'placa' ? 'AAA0A00' : '17 caracteres do chassi'}
            chassi={tipoIdentificador === 'chassi'}
          />
        </PlacaRow>

        <ServicosLabel>SERVIÇOS:</ServicosLabel>

        <ServicosGrid>
          {servicosConfig.map(function(servico) {
            const sel = selecoesServicos[servico.id] || { selecionado: false, texto: '' };

            if (servico.aceitaTexto) {
              return (
                <OutrosItem key={servico.id}>
                  <ServicoBox
                    selecionado={sel.selecionado}
                    onClick={function() { toggleServico(servico.id); }}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  >
                    {sel.selecionado && <Checkmark>&#10003;</Checkmark>}
                  </ServicoBox>
                  <OutrosLabel selecionado={sel.selecionado}>{servico.nome}:</OutrosLabel>
                  <OutrosInput
                    type="text"
                    value={sel.texto}
                    onChange={function(e) { alterarTextoServico(servico.id, e.target.value); }}
                    placeholder="descreva..."
                    maxLength={50}
                  />
                </OutrosItem>
              );
            }

            return (
              <ServicoItem
                key={servico.id}
                onClick={function() { toggleServico(servico.id); }}
                selecionado={sel.selecionado}
              >
                <ServicoBox selecionado={sel.selecionado}>
                  {sel.selecionado && <Checkmark>&#10003;</Checkmark>}
                </ServicoBox>
                <ServicoTexto selecionado={sel.selecionado}>
                  {servico.nome}
                </ServicoTexto>
              </ServicoItem>
            );
          })}
        </ServicosGrid>

        <ObsArea>
          {linhasObs.map(function(linha, i) {
            return (
              <LinhaObs key={i}>
                <ObsInput
                  type="text"
                  value={linha}
                  onChange={function(e) { handleObsChange(i, e.target.value); }}
                  placeholder={i === 0 ? 'Observações...' : ''}
                  destaque={i === 0}
                />
                <LinhaHr />
              </LinhaObs>
            );
          })}
        </ObsArea>

      </Folha>

      <ConfigServicosModal
        aberto={modalConfigAberto}
        onFechar={function() { setModalConfigAberto(false); }}
        onAtualizado={buscarServicos}
        addToast={addToast}
      />
    </PageWrapper>
  );
};

function formatarData(date) {
  return [
    String(date.getDate()).padStart(2, '0'),
    String(date.getMonth() + 1).padStart(2, '0'),
    date.getFullYear(),
  ].join('/');
}

function formatarHora(date) {
  return [
    String(date.getHours()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0'),
  ].join(':');
}

// ─── Estilos ──────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  background: #dde1e7;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media print { background: white; min-height: unset; }
`;

const PrintHide = styled.div`
  width: 100%;
  @media print { display: none !important; }
`;

const AcoesBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 32px 14px 66px;
  box-sizing: border-box;
  background: transparent;
`;

const AcoesTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  color: #1a2e3b;
  font-weight: 700;
  font-family: 'Segoe UI', Arial, sans-serif;
  white-space: nowrap;
`;

const BotaoImprimir = styled.button`
  display: flex;
  align-items: center;
  padding: 10px 22px;
  background: ${function(p) { return p.disabled ? '#aaa' : 'linear-gradient(135deg, #006d6d, #009999)'; }};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 700;
  font-family: 'Segoe UI', Arial, sans-serif;
  cursor: ${function(p) { return p.disabled ? 'not-allowed' : 'pointer'; }};
  letter-spacing: 0.5px;
  box-shadow: 0 3px 12px rgba(0,128,128,0.35);
  transition: all 0.22s;
  white-space: nowrap;
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0,128,128,0.4);
  }
  @media print { display: none; }
`;

const Folha = styled.div`
  position: relative;
  width: 210mm;
  min-height: 148mm;
  background: white;
  padding: 9mm 11mm 8mm 11mm;
  box-sizing: border-box;
  font-family: 'Arial Narrow', Arial, sans-serif;
  font-size: 10pt;
  box-shadow: 0 6px 30px rgba(0,0,0,0.22);
  margin: 14px 0 48px 0;
  @media print {
    width: 210mm;
    min-height: 148mm;
    margin: 0;
    padding: 9mm 11mm 8mm 11mm;
    box-shadow: none;
  }
`;

const EngrenagemBtn = styled.button`
  position: absolute;
  top: 9mm;
  right: 11mm;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1.5px solid #999;
  background: white;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  z-index: 5;
  &:hover { background: #e0f5f5; border-color: #006d6d; color: #006d6d; }
  @media print { display: none !important; }
`;

const TopoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
  margin-bottom: 5px;
`;

const EsquerdaBloco = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const EmpresaNome = styled.div`
  font-size: 12pt;
  font-weight: 800;
  text-transform: uppercase;
  color: #111;
  letter-spacing: 0.3px;
`;

const EmpresaInfo = styled.div`
  font-size: 10pt;
  font-weight: 600;
  text-transform: uppercase;
  color: #222;
`;

const UsuarioNome = styled.div`
  font-size: 10pt;
  font-weight: 700;
  text-transform: uppercase;
  color: #111;
  margin-top: 4px;
`;

const DireitaBloco = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  min-width: 160px;
  padding-right: 30px;
`;

const NumeroQuadro = styled.div`
  border: 2px solid #111;
  width: 34px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12pt;
  font-weight: 800;
  color: #111;
`;

const DataTexto = styled.div`
  font-size: 10pt;
  font-weight: 600;
  color: #111;
  text-align: right;
  text-transform: uppercase;
`;

const EncaminhanteWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 4px;
  width: 100%;
`;

const EncaminhanteInput = styled.input`
  border: none;
  border-bottom: 1.5px solid #111;
  outline: none;
  font-family: 'Arial Narrow', Arial, sans-serif;
  font-size: 10pt;
  font-weight: 700;
  width: 160px;
  text-align: left;
  background: transparent;
  padding: 2px 0;
  color: #111;
  &::placeholder { color: transparent; }
  @media print { border-bottom: 1.5px solid #111; }
`;

const EncaminhanteLabel = styled.div`
  font-size: 7.5pt;
  font-weight: 700;
  text-transform: uppercase;
  color: #555;
  text-align: right;
  margin-top: 2px;
  letter-spacing: 0.3px;
`;

/* ── NOVO: bloco "Nome do Cliente", separado do encaminhante ── */
const ClienteWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 6px;
  width: 100%;
`;

const ClienteInput = styled.input`
  border: none;
  border-bottom: 1.5px solid #111;
  outline: none;
  font-family: 'Arial Narrow', Arial, sans-serif;
  font-size: 10pt;
  font-weight: 700;
  width: 160px;
  text-align: left;
  background: transparent;
  padding: 2px 0;
  color: #111;
  &::placeholder { color: transparent; }
  @media print { border-bottom: 1.5px solid #111; }
`;

const ClienteLabel = styled.div`
  font-size: 7.5pt;
  font-weight: 700;
  text-transform: uppercase;
  color: #555;
  text-align: right;
  margin-top: 2px;
  letter-spacing: 0.3px;
`;

const Divisor = styled.hr`
  border: none;
  border-top: 1.5px solid #111;
  margin: 6px 0;
`;

const PdfRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-bottom: 8px;
  min-height: 26px;
  @media print { display: none !important; }
`;

const PdfLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5pt;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
  color: #111;
  user-select: none;
`;

const PdfCheckbox = styled.input`
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #006d6d;
`;

const QtdBox = styled.div`
  border: 2px solid #111;
  width: 26px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10pt;
  font-weight: 700;
  color: #111;
`;

const RoundBtn = styled.button`
  width: 20px;
  height: 20px;
  border: 1.5px solid #555;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: #333;
  transition: all 0.18s;
  &:hover { background: #e0f5f5; border-color: #006d6d; color: #006d6d; }
  @media print { display: none; }
`;

/* Linha PDF visível SOMENTE na impressão */
const PdfRowPrint = styled.div`
  display: none;
  @media print {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    min-height: 20px;
  }
`;

const PdfPrintTexto = styled.span`
  font-size: 10pt;
  font-weight: 700;
  text-transform: uppercase;
  color: #111;
`;

/* ── PLACA / CHASSI ──────────────────────────────────────────────────────────── */
const PlacaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const TipoToggle = styled.div`
  display: flex;
  gap: 4px;
  margin-right: 4px;
  @media print { display: none !important; }
`;

const TipoBtn = styled.button`
  font-size: 7.5pt;
  font-weight: 800;
  letter-spacing: 0.3px;
  padding: 3px 8px;
  border-radius: 10px;
  border: 1.5px solid ${function(p) { return p.ativo ? '#006d6d' : '#ccc'; }};
  background: ${function(p) { return p.ativo ? '#e0f5f5' : 'white'; }};
  color: ${function(p) { return p.ativo ? '#006d6d' : '#999'; }};
  cursor: pointer;
  transition: all 0.15s;
`;

const PlacaLabel = styled.span`
  font-size: 10.5pt;
  font-weight: 800;
  text-transform: uppercase;
  color: #111;
  white-space: nowrap;
`;

const PlacaInput = styled.input`
  border: none;
  border-bottom: 1.5px solid #111;
  outline: none;
  font-family: 'Arial Narrow', Arial, sans-serif;
  font-size: 10.5pt;
  font-weight: 700;
  width: ${function(p) { return p.chassi ? '190px' : '110px'; }};
  background: transparent;
  padding: 2px 4px;
  color: #111;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: width 0.15s;
  &::placeholder { color: #bbb; font-style: italic; font-weight: 400; letter-spacing: 0; }
  @media print {
    border-bottom: 1.5px solid #111;
    &::placeholder { color: transparent !important; }
  }
`;

/* ─────────────────────────────────────────────────────────────────────────── */

const ServicosLabel = styled.div`
  font-size: 10.5pt;
  font-weight: 800;
  text-transform: uppercase;
  color: #111;
  margin-bottom: 7px;
`;

const ServicosGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: row;
  row-gap: 9px;
  column-gap: 14px;
  margin-bottom: 10px;
`;

const ServicoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const ServicoBox = styled.div`
  width: 15px;
  height: 15px;
  min-width: 15px;
  border: 2px solid ${function(p) { return p.selecionado ? '#006d6d' : '#555'; }};
  background: ${function(p) { return p.selecionado ? '#006d6d' : 'white'; }};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  transition: all 0.15s;
`;

const Checkmark = styled.span`
  color: white;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
`;

const ServicoTexto = styled.span`
  font-size: 9.5pt;
  text-transform: uppercase;
  font-weight: ${function(p) { return p.selecionado ? '700' : '500'; }};
  color: ${function(p) { return p.selecionado ? '#006d6d' : '#111'; }};
  border-bottom: 1.5px solid #111;
  flex: 1;
  padding-bottom: 2px;
  transition: color 0.15s;
`;

const OutrosItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OutrosLabel = styled.span`
  font-size: 9.5pt;
  font-weight: ${function(p) { return p.selecionado ? '800' : '700'; }};
  text-transform: uppercase;
  color: ${function(p) { return p.selecionado ? '#006d6d' : '#111'; }};
  white-space: nowrap;
  transition: color 0.15s;
`;

const OutrosInput = styled.input`
  flex: 1;
  border: none;
  border-bottom: 1.5px solid #111;
  outline: none;
  font-family: 'Arial Narrow', Arial, sans-serif;
  font-size: 9.5pt;
  font-weight: 600;
  background: transparent;
  padding: 2px 0;
  color: #111;
  min-width: 0;
  &::placeholder { color: #bbb; font-style: italic; font-weight: 400; }
  @media print {
    border-bottom: 1.5px solid #111;
    &::placeholder { color: transparent !important; }
  }
`;

const ObsArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 4px;
`;

const LinhaObs = styled.div``;

const ObsInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-family: 'Arial Narrow', Arial, sans-serif;
  font-size: ${function(p) { return p.destaque ? '20pt' : '9.5pt'; }};
  font-weight: 700;
  background: transparent;
  padding: ${function(p) { return p.destaque ? '2px 0 5px 0' : '0 0 3px 0'; }};
  box-sizing: border-box;
  color: #111;
  &::placeholder { color: #bbb; font-style: italic; font-weight: 400; }
  @media print {
    &::placeholder { color: transparent !important; }
  }
`;

const LinhaHr = styled.hr`
  border: none;
  border-top: 1.5px solid #111;
  margin: 0;
`;

export default ProtocoloAtendimento;
